import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { Row, Col, Label } from 'reactstrap';
import CustomSelect from 'components/CustomSelect';
import { formResMessage, formatMoney } from 'utils/commonFunc';
import { fetchTransportFeeReportApi } from 'actions/admin/transport';
import ReportTable from './ReportTable';

const ALL = '';
const STATUS = { PAID: 'Paid', DUE: 'Fee due', UP_TO_DATE: 'Up to date' };
const STATUS_OPTIONS = [
  { value: ALL, label: 'All students' },
  ...Object.entries(STATUS).map(([value, label]) => ({ value, label })),
];
const COLUMNS = [
  { key: 'student', label: 'Student' },
  { key: 'course', label: 'Class' },
  { key: 'stop', label: 'Stop' },
  { key: 'monthly', label: 'Monthly fee' },
  { key: 'paid', label: 'Paid' },
  { key: 'due', label: 'Due now' },
  { key: 'balance', label: 'Year balance' },
  { key: 'pendingMonths', label: 'Pending months' },
  { key: 'status', label: 'Status' },
  { key: 'guardian', label: 'Guardian' },
  { key: 'mobile', label: 'Mobile' },
];
const sum = (rows, key) => rows.reduce((acc, row) => acc + Number(row.raw[key] || 0), 0);

// Transport fee pending, route by route (Transport reports): who has paid the bus fee month by month and who still owes it, with
// the months left and the guardian's number to call. `routes` are the school's routes (their transportFeeStructId is the id the
// backend report takes).
const FeePendingReport = ({ batchId, loggedUserToken, showWarning, routes }) => {
  const [routeId, setRouteId] = useState(routes[0]?.transportFeeStructId ?? null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('DUE');

  useEffect(() => {
    if (!routeId || !batchId) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchTransportFeeReportApi({ routeId, batchId, token: loggedUserToken })
      .then(res => !cancelled && setRecords(res.data || []))
      .catch(err => {
        if (cancelled) return;
        setRecords([]);
        showWarning(formResMessage(err), 'error');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [routeId, batchId]);

  const rows = useMemo(
    () =>
      records.map(item => ({
        raw: item,
        student: item.studentName,
        course: item.courseName || '--',
        stop: item.areaName || '--',
        monthly: formatMoney(item.monthlyFee),
        paid: formatMoney(item.paidAmount),
        due: formatMoney(item.dueAmount),
        balance: formatMoney(item.balanceAmount),
        pendingMonths: (item.pendingMonths || []).join(', ') || '--',
        status: STATUS[item.paymentStatus] || item.paymentStatus,
        guardian: item.guardianName || '--',
        mobile: item.guardianMobile || '--',
        tones: item.paymentStatus === 'DUE' ? { status: 'bad', due: 'bad' } : {},
      })),
    [records]
  );
  const shown = rows.filter(row => !status || row.raw.paymentStatus === status);

  const filters = (
    <Row>
      <Col lg={4} md={6} sm={6} xs={12} className="mb-2">
        <Label className="form-label">Route</Label>
        <CustomSelect
          noLabel
          usePortal
          name="feeRoute"
          value={routeId}
          options={routes.map(route => ({ value: route.transportFeeStructId, label: route.description }))}
          onChange={(n, v) => setRouteId(v)}
        />
      </Col>
      <Col lg={4} md={6} sm={6} xs={12} className="mb-2">
        <Label className="form-label">Status</Label>
        <CustomSelect
          noLabel
          usePortal
          name="feeStatus"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(n, v) => setStatus(v)}
        />
      </Col>
    </Row>
  );

  return (
    <ReportTable
      title="Transport fee pending"
      fileName="transport_fee_pending"
      columns={COLUMNS}
      rows={shown}
      filters={filters}
      summary={[
        { label: 'Students', value: shown.length },
        { label: 'Fee due', value: shown.filter(row => row.raw.paymentStatus === 'DUE').length, tone: 'bad' },
        { label: 'Collected', value: formatMoney(sum(shown, 'paidAmount')), tone: 'good' },
        { label: 'Due now', value: formatMoney(sum(shown, 'dueAmount')), tone: 'bad' },
      ]}
      emptyText="No students for this route and filter."
      loading={loading}
    />
  );
};

export default FeePendingReport;
